import React, { useContext, useEffect, useRef, useState } from 'react';
import { Prompt } from 'react-router-dom';

import {
  videoSupportedExtensions,
  mbVideoMaxSize,
  confirmationMsgLetUploadVideo,
  ImageSupportedExtensions,
  documentSupportedExtensions,
  audioSupportedExtensions,
} from 'config/constants';
import { CrossIcon, FileIcon } from 'assets/icons';
import { NotificationType, VideoStatus } from 'common/enums';
import { Button, ButtonSize, ButtonStyle } from 'common/button';
import { FileUploaderVideo } from 'common/file-uploader-video';

import { LocalStorageApi } from 'helpers/local-storage';
import { directUploadFile } from 'helpers/direct-upload';
import {
  getContentTypeFromFile,
  removeFileExtension,
} from 'helpers/file-utils';
import { isOutOfStorageError, storageFits } from 'helpers/storage';
import { SelfRecordingContext } from 'context/self-recording-context';
import { VaultsController } from 'networking/controllers/vaults-controller';
import { AppContext, appActions } from 'context/app-context';
import { NotificationObject } from 'models/notificationObject';
import { VideosRecordedType } from 'context/self-recording-context/context-reducer';
import { selfRecordingActions } from 'context/self-recording-context/action-types';
import { AllVideoProps } from 'networking/types/all-video-props';
import { publishMemoryAdded } from 'common/returning-onboarding/onboarding-status';
import { VideosQueue } from '../videos-queue';
import styles from './upload-videos.module.scss';

type UploadVideosProps = {
  vault: VaultType;
  refetchVault?: () => void;
  browseFilesRef?: React.RefObject<HTMLDivElement | null>;
  isEmptyState?: boolean;
  // Called when the user confirms the upload (Save) in the empty-state modal.
  onDone?: () => void;
};

const UploadVideos: React.FC<UploadVideosProps> = ({
  vault,
  refetchVault,
  browseFilesRef,
  isEmptyState = false,
  onDone,
}) => {
  const {
    state: { selectedQuestion, videosRecorded, batchId, albumId, entryMeta },
    dispatch,
  } = useContext(SelfRecordingContext);
  const { state: appState, dispatch: generalDispatch } = useContext(AppContext);
  const [videos, setVideos] = useState<AllVideoProps[]>([]);
  const videoId = useRef<number>(0);
  const guestToken = LocalStorageApi.get('guestToken');
  const [nextQuestion, setNextQuestion] = useState(false);
  const currentQuestionIndex = useRef<number>(0);

  const addNewVideoToList = (fileReceived: File) => {
    videoId.current += 1;
    const newFile: AllVideoProps = {
      file: fileReceived,
      id: videoId.current,
      status: VideoStatus.new,
      uploadStartTime: new Date(),
      uploadId: videoId.current.toString(),
      contentType: getContentTypeFromFile(fileReceived),
    };
    setVideos((prevVideos) => [...prevVideos, newFile]);
  };

  const removeQueuedFile = (id: number) =>
    setVideos((prevVideos) => prevVideos.filter((video) => video.id !== id));

  // Modal (empty-state) flow: dropped files are only QUEUED — the S3 upload
  // and the memory creation both start on Save. The promise chain owns no
  // component state, so the modal can close immediately while the work
  // continues, driving the album grid's placeholder card via context.
  const uploadQueuedFile = async (video: AllVideoProps) => {
    const name = removeFileExtension(video.file.name);
    const upload = {
      type: video.contentType,
      name,
      data: video.file.name,
      uploadId: video.uploadId,
    };
    // Pre-check against the vault's storage plan; the server enforces this
    // too (507 out_of_storage), this just fails fast with a clear message.
    if (!storageFits(vault, video.file.size)) {
      generalDispatch({
        type: appActions.NOTIFICATION,
        notification: new NotificationObject({
          show: true,
          title: 'You’re out of storage',
          message: `${video.file.name} doesn't fit in this vault's remaining storage.`,
          type: NotificationType.Error,
        }),
      });
      return;
    }
    dispatch({
      type: selfRecordingActions.addUpload,
      addUpload: { ...upload, isUploading: true },
    });
    try {
      const uploaded = await directUploadFile(video.file);
      const createClipRequest: CreateClipRequestType = {
        clip: {
          videoSignedId: uploaded.signedId,
          name,
          recordedAt: video.uploadStartTime ?? new Date(Date.now()),
          albumId,
          // Date/location picked in the Add Entry dialog, if any.
          ...entryMeta,
        },
      };
      if (guestToken) {
        await VaultsController.createDocumentWithToken(
          albumId!,
          vault.id,
          createClipRequest,
        );
      } else {
        await VaultsController.createDocument(vault.id, createClipRequest);
      }
      dispatch({
        type: selfRecordingActions.updateUpload,
        updateUpload: { ...upload, isUploading: false, error: false },
      });
    } catch (err) {
      dispatch({
        type: selfRecordingActions.updateUpload,
        updateUpload: { ...upload, isUploading: false, error: true },
      });
      const outOfStorage = isOutOfStorageError(err);
      generalDispatch({
        type: appActions.NOTIFICATION,
        notification: new NotificationObject({
          show: true,
          title: outOfStorage
            ? 'You’re out of storage'
            : `${video.file.name} could not be uploaded`,
          message: outOfStorage
            ? `${video.file.name} doesn't fit in this vault's remaining storage.`
            : 'Please try again',
          type: NotificationType.Error,
        }),
      });
    }
  };

  const handleModalSave = () => {
    if (guestToken && !albumId) {
      generalDispatch({
        type: appActions.NOTIFICATION,
        notification: new NotificationObject({
          show: true,
          title: 'Something went wrong',
          message: 'No topic selected. Please select a topic and try again.',
          type: NotificationType.Error,
        }),
      });
      return;
    }
    const queued = [...videos];
    onDone?.();
    queued.forEach((video) => {
      uploadQueuedFile(video);
    });
  };

  const totalVideosInProgress = () =>
    videos.filter(
      (video: AllVideoProps) => video.status === VideoStatus.inProgress,
    ).length;

  const handleChangeVideoStatus = (id: number, newStatus: string) => {
    setVideos(
      videos.map((video: AllVideoProps) => {
        if (video.id === id) {
          return { ...video, status: newStatus };
        }
        return video;
      }),
    );
  };

  useEffect(() => {
    if (videosRecorded.length > 1 && !batchId) {
      dispatch({
        type: selfRecordingActions.setBatchId,
      });
    }
    if (videosRecorded.length === 0) {
      setVideos([]);
      if (refetchVault) {
        refetchVault();
        if (nextQuestion) {
          setNextQuestion(false);
          if (
            vault.unansweredQuestions &&
            vault.unansweredQuestions?.length >= currentQuestionIndex.current
          ) {
            const questionSelected =
              vault.unansweredQuestions[currentQuestionIndex.current + 1];

            if (questionSelected) {
              dispatch({
                type: selfRecordingActions.setSelectedQuestion,
                selectedQuestion: questionSelected,
              });
            } else {
              dispatch({
                type: selfRecordingActions.setSelectedQuestion,
                selectedQuestion: vault.unansweredQuestions[0],
              });
            }
          }
          return;
        }
        if (selectedQuestion) {
          const questionSelected = vault.unansweredQuestions?.find(
            (item) => item.id === selectedQuestion?.id,
          );
          dispatch({
            type: selfRecordingActions.setSelectedQuestion,
            selectedQuestion: questionSelected,
          });
        }
      }
    }
  }, [videosRecorded]);

  const createClip = async (
    video: VideosRecordedType,
    vaultId: number,
    name: string,
    positionInBatch: number,
  ) => {
    try {
      const createClipRequest: CreateClipRequestType = {
        clip: {
          videoSignedId: video.videoSignedId,
          name,
          recordedAt: video.uploadStartTime ?? new Date(Date.now()),
          batchId,
          positionInBatch,
          albumId,
          ...entryMeta,
        },
      };
      if (guestToken && !albumId) {
        generalDispatch({
          type: appActions.NOTIFICATION,
          notification: new NotificationObject({
            show: true,
            title: 'Something went wrong',
            message: 'No topic selected. Please select a topic and try again.',
            type: NotificationType.Error,
          }),
        });
        return;
      }

      const createDocumentResponse = guestToken
        ? await VaultsController.createDocumentWithToken(
            albumId!,
            vaultId,
            createClipRequest,
          )
        : await VaultsController.createDocument(vaultId, createClipRequest);
      publishMemoryAdded();

      dispatch({
        type: selfRecordingActions.setUploadResponse,
        uploadResponse: {
          uploadId: video.requestId,
          response: {
            ...createDocumentResponse,
            fileUrl: createDocumentResponse.fileUrl || video.videoUrl,
            contentType:
              createDocumentResponse.contentType || video.contentType,
            user: appState.data.user,
          },
        },
      });

      dispatch({
        type: selfRecordingActions.updateUpload,
        updateUpload: {
          uploadId: video.requestId,
          type: video.contentType,
          data: video.fileName,
          error: false,
          isUploading: false,
        },
      });
    } catch (error) {
      generalDispatch({
        type: appActions.NOTIFICATION,
        notification: new NotificationObject({
          show: true,
          title: 'Something went wrong',
          message: 'Please try again',
          type: NotificationType.Error,
        }),
      });
      dispatch({
        type: selfRecordingActions.updateUpload,
        updateUpload: {
          uploadId: video.requestId,
          type: video.contentType,
          data: video.fileName,
          error: true,
          isUploading: false,
        },
      });
    }
  };

  const handleSaveClips = async () => {
    try {
      Promise.all(
        videosRecorded.map((video, index) =>
          createClip(video, vault.id, video.fileName ?? '', index + 1),
        ),
      ).then(() =>
        dispatch({
          type: selfRecordingActions.setBatchId,
        }),
      );
      return true;
    } catch (error) {
      generalDispatch({
        type: appActions.NOTIFICATION,
        notification: new NotificationObject({
          show: true,
          title: 'Something went wrong',
          message: 'Please try again',
          type: NotificationType.Error,
        }),
      });
      return false;
    }
  };

  const saveVideosCustomRecording = async () => {
    const response = await handleSaveClips();
    if (response) {
      setVideos([]);
      dispatch({
        type: selfRecordingActions.deleteAllVideos,
      });
    }
  };
  useEffect(
    () => () => {
      setVideos([]);
      if (!guestToken) {
        dispatch({ type: selfRecordingActions.deleteAllVideos });
      }
    },
    [],
  );

  const saveVideosSelectedQuestion = async () => {
    await handleSaveClips();
  };

  useEffect(() => {
    if (
      videos.length &&
      videos.every((video) => video.status === VideoStatus.ready)
    ) {
      if (selectedQuestion && !guestToken) {
        saveVideosSelectedQuestion();
      }
    }
    if (!videos.length) {
      dispatch({
        type: selfRecordingActions.setIsUploading,
        isUploading: false,
      });
    }
  }, [videos]);

  useEffect(() => {
    const videosInProgress = videos.some(
      (video) => video.status === VideoStatus.inProgress,
    );
    dispatch({
      type: selfRecordingActions.setIsUploading,
      isUploading: videosInProgress,
    });
  }, [videos]);

  return (
    <>
      <Prompt
        when={!!totalVideosInProgress()}
        message={confirmationMsgLetUploadVideo}
      />
      <FileUploaderVideo
        supportedExtensions={[
          ...videoSupportedExtensions,
          ...ImageSupportedExtensions,
          ...documentSupportedExtensions,
          ...audioSupportedExtensions,
        ]}
        maxFileSizeInMb={mbVideoMaxSize}
        notifyNewFile={addNewVideoToList}
        canUploadMultipleFiles={!selectedQuestion}
        browseFilesRef={browseFilesRef}
        isEmptyState={isEmptyState}
      />
      {/* Recording flows: the hidden queue uploads + auto-saves as before.
          The modal (empty-state) flow never mounts it — see handleModalSave. */}
      {videos.length > 0 && !isEmptyState && (
        <div className={styles.noShow}>
          <VideosQueue
            vaultId={vault.id}
            videos={videos}
            notifyVideoStatusChange={handleChangeVideoStatus}
            videosInProgress={!!totalVideosInProgress()}
            saveVideos={saveVideosCustomRecording}
          />
        </div>
      )}

      {isEmptyState && videos.length > 0 && (
        <div className={styles.uploadList}>
          {videos.map((video) => (
            <div key={video.id} className={styles.uploadItem}>
              <span className={styles.uploadFileIcon}>
                <FileIcon />
              </span>
              <span className={styles.uploadName}>{video.file.name}</span>
              <button
                type="button"
                className={styles.uploadRemove}
                aria-label={`Remove ${video.file.name}`}
                onClick={() => removeQueuedFile(video.id)}
              >
                <CrossIcon />
              </button>
            </div>
          ))}
        </div>
      )}

      {isEmptyState && (
        <div className={styles.footer}>
          <Button
            buttonSize={ButtonSize.Medium}
            buttonStyle={ButtonStyle.Primary}
            disabled={videos.length === 0}
            onClick={handleModalSave}
          >
            Save
          </Button>
        </div>
      )}
    </>
  );
};

export { UploadVideos };
